const MILLI_PER_PIECE = 1000

export type EbomPartInstanceAllocation = {
  ebomPartId: string
  milliQuantity: number
  instanceIndexes: number[] | null
}

export type InstanceAllocationChange = {
  milliQuantity: number
  instanceIndexes: number[] | null
}

export type InstanceAllocationRequest = {
  ebomMilliQuantity: number
  instanceIndex: number | null
  allInstances: boolean
}

export type InstanceAllocationDecision =
  | { kind: 'change'; change: InstanceAllocationChange; allocates: boolean }
  | { kind: 'remove' }

export function findInstanceAllocation(
  allocations: EbomPartInstanceAllocation[],
  ebomPartId: string,
): EbomPartInstanceAllocation | null {
  return allocations.find((a) => a.ebomPartId === ebomPartId) ?? null
}

export function instanceCountOf(milliQuantity: number | undefined): number {
  return Math.floor((milliQuantity ?? 0) / MILLI_PER_PIECE)
}

export function allocatesAllInstances(
  allocation: EbomPartInstanceAllocation | null,
  total: number,
): boolean {
  return (
    !!allocation &&
    !allocation.instanceIndexes &&
    allocation.milliQuantity >= total * MILLI_PER_PIECE
  )
}

export function needsInstanceSelection(
  allocation: EbomPartInstanceAllocation | null,
  total: number,
): boolean {
  if (!allocation || allocation.instanceIndexes || total < 2) return false
  const { milliQuantity } = allocation
  return (
    milliQuantity > 0 &&
    milliQuantity < total * MILLI_PER_PIECE &&
    milliQuantity % MILLI_PER_PIECE === 0
  )
}

export function allocatedInstanceIndexes(
  allocation: EbomPartInstanceAllocation | null,
  total: number,
): number[] {
  if (!allocation) return []
  if (allocation.instanceIndexes) return allocation.instanceIndexes
  return allocatesAllInstances(allocation, total) ? [...Array(total).keys()] : []
}

export function isInstanceAllocated(
  allocation: EbomPartInstanceAllocation | null,
  total: number,
  index: number,
): boolean {
  return allocatedInstanceIndexes(allocation, total).includes(index)
}

export function selectingInstances(indexes: number[], total: number): InstanceAllocationChange {
  const unique = [...new Set(indexes)].sort((a, b) => a - b)
  if (unique.length >= total) return allocatingAllInstances(total)
  return { milliQuantity: unique.length * MILLI_PER_PIECE, instanceIndexes: unique }
}

export function addingInstance(
  allocation: EbomPartInstanceAllocation | null,
  total: number,
  index: number,
): InstanceAllocationChange {
  return selectingInstances([...allocatedInstanceIndexes(allocation, total), index], total)
}

export function removingInstance(
  allocation: EbomPartInstanceAllocation | null,
  total: number,
  index: number,
): InstanceAllocationChange {
  return selectingInstances(
    allocatedInstanceIndexes(allocation, total).filter((i) => i !== index),
    total,
  )
}

export function allocationChangeFor(
  request: InstanceAllocationRequest,
  allocation: EbomPartInstanceAllocation | null,
): InstanceAllocationDecision {
  const wholePart = { milliQuantity: request.ebomMilliQuantity, instanceIndexes: null }
  const { instanceIndex } = request
  if (request.allInstances) return { kind: 'change', change: wholePart, allocates: true }
  if (instanceIndex === null) {
    return allocation ? { kind: 'remove' } : { kind: 'change', change: wholePart, allocates: true }
  }
  const total = instanceCountOf(request.ebomMilliQuantity)
  if (!isInstanceAllocated(allocation, total, instanceIndex)) {
    return {
      kind: 'change',
      change: addingInstance(allocation, total, instanceIndex),
      allocates: true,
    }
  }
  const change = removingInstance(allocation, total, instanceIndex)
  return change.milliQuantity === 0
    ? { kind: 'remove' }
    : { kind: 'change', change, allocates: false }
}

export function allocatingAllInstances(total: number): InstanceAllocationChange {
  return { milliQuantity: total * MILLI_PER_PIECE, instanceIndexes: null }
}
